import { Sparkles } from "lucide-react";

const COLE_2022_URL = "https://pubmed.ncbi.nlm.nih.gov/35624717/";
const COLE_2020_URL = "https://pubmed.ncbi.nlm.nih.gov/32552917/";
const FDA_510K_URL =
  "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm";
const STANFORD_LAB_URL = "https://med.stanford.edu/brainstimulationlab.html";

export const SaintOutcomes = () => (
  <section className="py-16 sm:py-20 bg-background">
    <div className="container-px mx-auto max-w-6xl px-4">
      <div className="text-center mb-10 sm:mb-12">
        <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-accent">
          <Sparkles className="h-4 w-4" />
          Clinical Research
        </span>
        <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          What the research shows
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            value: "79%",
            label:
              "Remission rate in the double-blind randomized controlled trial",
            source: "Cole et al., American Journal of Psychiatry, 2022",
            href: COLE_2022_URL,
          },
          {
            value: "2.6 days",
            label: "Average time to remission in the open-label study",
            source: "Cole et al., American Journal of Psychiatry, 2020",
            href: COLE_2020_URL,
          },
          {
            value: "5 days",
            label:
              "Total treatment length, versus 30–36 visits over 6–9 weeks for standard TMS",
            source: "Standard TMS protocol comparison",
          },
          {
            value: "400+",
            label:
              "Patients treated in clinical trials with no serious adverse events reported",
            source: "Magnus Medical clinical research",
            href: "https://www.magnusmedical.com/clinical-research/",
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft"
          >
            <p className="font-headings text-3xl font-extrabold text-accent sm:text-4xl">
              {stat.value}
            </p>
            <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
              {stat.label}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {stat.href ? (
                <a
                  href={stat.href}
                  target="_blank"
                  rel="noopener"
                  className="font-semibold text-accent underline underline-offset-2 hover:text-primary"
                >
                  {stat.source}
                </a>
              ) : (
                stat.source
              )}
            </p>
          </div>
        ))}
      </div>

      {/* 12-month sustained remission callout */}
      <div className="mt-8 mx-auto max-w-3xl rounded-2xl border-2 border-accent/25 bg-gradient-to-br from-accent/[0.06] to-transparent p-6 sm:p-8 shadow-soft">
        <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-accent/15 text-accent">
            <Sparkles className="h-7 w-7" />
          </div>
          <div>
            <h3 className="font-headings text-lg font-bold text-primary">
              Lasting results — 12-month sustained remission
            </h3>
            <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
              Published research on SAINT® includes a study demonstrating
              sustained remission at 12 months when paired with a personalized
              continuation and maintenance therapy plan. Your care team builds
              an individualized follow-up strategy to help protect the progress
              you make during your treatment week.
            </p>
          </div>
        </div>
      </div>

      <p className="mt-8 text-center text-sm text-muted-foreground leading-relaxed max-w-3xl mx-auto">
        Results from clinical trials in moderately to severely
        treatment-resistant depression. Individual results vary. Sources:{" "}
        <a
          href="https://www.magnusmedical.com/clinical-research/"
          target="_blank"
          rel="noopener"
          className="font-semibold text-accent underline underline-offset-2 hover:text-primary"
        >
          Magnus Medical clinical research
        </a>
        ;{" "}
        <a
          href={COLE_2020_URL}
          target="_blank"
          rel="noopener"
          className="font-semibold text-accent underline underline-offset-2 hover:text-primary"
        >
          Cole et al. 2020
        </a>
        ;{" "}
        <a
          href={COLE_2022_URL}
          target="_blank"
          rel="noopener"
          className="font-semibold text-accent underline underline-offset-2 hover:text-primary"
        >
          Cole et al. 2022
        </a>
        . SAINT is an FDA-cleared treatment — see the{" "}
        <a
          href={FDA_510K_URL}
          target="_blank"
          rel="noopener"
          className="font-semibold text-accent underline underline-offset-2 hover:text-primary"
        >
          FDA 510(k) record
        </a>
        . Protocol developed by the{" "}
        <a
          href={STANFORD_LAB_URL}
          target="_blank"
          rel="noopener"
          className="font-semibold text-accent underline underline-offset-2 hover:text-primary"
        >
          Stanford Brain Stimulation Lab
        </a>
        .
      </p>
    </div>
  </section>
);
